import Button from '@/components/Button';
import Link from '@/components/Link';
import IconV2 from '@/components/v2/IconV2';
import Stack from '@/components/v3/Stack';
import useActiveCursor from '@/hooks/useActiveCursor';
import usePlatformNav from '@/hooks/usePlatformNav';
import useResponsive from '@/hooks/useResponsive.js';
import { openAppIssueReportDrawer } from '@/redux/actions/uiActions/drawerActions';
import { openAuditLogSlider } from '@/redux/actions/uiActions/setAuditLogSlider';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import routes from '@/routes';
import appsPageSlugs from '@/screens/apps/lib/appsPageSlugs.js';
import AppScreenActions from '@/screens/apps/appEditorPage/AppScreenActions';
import JobScreenActions from '@/screens/jobs/JobScreenActions';
import WorkflowScreenActions from '@/screens/workflows/workflowEditor/WorkflowScreenActions';
import { useRef, useState } from 'react';
import { IoBugOutline } from 'react-icons/io5';
import { VscRequestChanges } from 'react-icons/vsc';
import { useSelector } from 'react-redux';

const PlatformSecondaryNav = () => {
  const items = usePlatformNav();
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const isDraftWorkflow = useSelector((state) => selectors.param(state, 'workflowVersion') === 'draft');
  const jobId = useArgSelector(selectors.param, 'jobId');
  const appId = useArgSelector(selectors.param, 'appId');
  const credentialId = useArgSelector(selectors.param, 'credentialId');
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const { mobile } = useResponsive();
  const activeRoute = routes.find((n) => n.id === activeRouteId);
  const [hoverStyle, setHoverStyle] = useState({});
  const navRef = useRef(null);

  const activeIndex = items.findIndex((item) => item.active); // Get active index from items
  const cursorStyle = useActiveCursor({ activeIndex, containerRef: navRef, offset: -12 });

  const handleHover = (index) => {
    const hoverItem = navRef.current?.children[index];
    if (hoverItem) {
      const offsetLeft = hoverItem.offsetLeft + 0;
      const offsetWidth = hoverItem.offsetWidth;
      setHoverStyle({
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`
      });
    }
  };

  const isAppEditorRoute = appId && !appsPageSlugs.has(appId);

  return (
    <Stack justify={'between'} className={`mc-secondary-nav ${workflowId || jobId || isAppEditorRoute ? 'has-actions' : ''} route-${activeRoute?.routeId} ${isDraftWorkflow ? 'draft' : ''}`}>
      <Stack gap={'xs'} justify={'start'} className={`route-${activeRoute?.routeId}`}>
        <div ref={navRef} className={'nav-items'}>
          {items.map((item, index) => (
            <Link key={item.label} to={item.route} className={`mc-nav-item ${item.active ? 'active' : ''} ${item.type === 'return' ? 'return' : ''}`} onMouseEnter={() => handleHover(index)} replace={false}>
              {item.icon ? <IconV2 icon={item.icon} title={item.label} /> : item.label}
              {activeRoute?.routeId === 'adminSettings' && item.type === 'return' ? <span className={'return-label'}>Home</span> : null}
              {item.rightIcon ? <IconV2 className={'right-icon'} icon={item.rightIcon} title={item.label} /> : ''}
            </Link>
          ))}
        </div>
        <div className="mc-secondary-nav-hover-bg" style={hoverStyle} />
        <div className="mc-secondary-nav-active-cursor" style={cursorStyle} />
      </Stack>
      {(workflowId || jobId || isAppEditorRoute || credentialId) && (
        <Stack gap={8} className="secondary-nav-actions-container">
          {workflowId && <WorkflowScreenActions />}
          {jobId && <JobScreenActions />}
          {isAppEditorRoute && <AppScreenActions />}
          {!mobile && isAppEditorRoute && <Button onClick={openAppIssueReportDrawer} icon={IoBugOutline} title="Report Defect" className="logs-button ghost" />}
          {!mobile && (workflowId || isAppEditorRoute || credentialId) && <Button onClick={openAuditLogSlider} icon={VscRequestChanges} title="View Audit Logs" className={`logs-button ghost ${mobile ? 'outline' : ''}`} />}
        </Stack>
      )}
    </Stack>
  );
};

export default PlatformSecondaryNav;
